import { API_HOST } from './config'
import type { JoinErrorKind } from './types'

/**
 * Client half of the join funnel. The server goes blind after it mints a token:
 * whether media actually flowed, which ICE path was used, whether the browser
 * blocked the camera — only this app knows. Without these reports a class can
 * fail completely while every server metric stays green.
 *
 * Fire-and-forget by design: telemetry must never delay or break a join.
 */

const DIAGNOSTICS_PATH = '/api/v1/public/livekit/diagnostics'

/**
 * Whether `fetch` honours `keepalive`. Probed once: reading it off
 * Request.prototype is the only way to tell, since a browser that ignores the
 * option accepts it silently and simply drops the request on unload.
 */
const KEEPALIVE_SUPPORTED = (() => {
  try {
    return 'keepalive' in new Request('')
  } catch {
    return false
  }
})()

export type FunnelStage =
  | 'permissions'
  | 'signalling_connected'
  | 'peer_present'
  | 'first_media'
  | 'stable_media'
  | 'left'
  | 'join_failed'

// Injected at build time from package.json (vite define) — bump the package
// version with each feature wave and every telemetry row carries it. The
// fallback only exists for tools that evaluate this module outside Vite.
export const MEET_VERSION = typeof __MEET_VERSION__ === 'string' ? __MEET_VERSION__ : 'dev'

/**
 * One id per join attempt, generated at boot and attached to every stage
 * report. Stage counts alone cannot be turned into conversion rates — you need
 * to know that THIS attempt reached signalling but never reached media.
 */
export const JOIN_ATTEMPT_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

/** Short, human-quotable id so support can find one specific failure in logs. */
export function newFailureId(): string {
  return Math.random().toString(36).slice(2, 8).toUpperCase()
}

interface Credential {
  code?: string | null
  reconnectToken?: string | null
}

interface ReportOptions {
  stage: FunnelStage
  outcome?: 'ok' | 'error'
  errorKind?: JoinErrorKind | string
  durationMs?: number
  failureId?: string
  ice?: string
  permission?: string
}

/**
 * ORDER IS LOAD-BEARING, and it used to be wrong.
 *
 * Edge's UA string CONTAINS "Chrome" — `...Chrome/120.0.0.0 Safari/537.36
 * Edg/120.0.0.0` — so testing for Chrome first matched every Edge user and made
 * the `Edg` branch unreachable. Every Edge session in production was recorded as
 * Chrome, which meant "our students are on Chrome" was a claim the data could not
 * actually support, and an Edge-specific failure would have been invisible.
 *
 * Same trap for Opera (`OPR`), which also carries "Chrome". Brave deliberately
 * reports itself as Chrome and cannot be distinguished — that one is by design.
 *
 * userAgentData is preferred where present: brands are explicit ("Microsoft
 * Edge") and survive the UA reduction that keeps making these strings less
 * useful. The UA path stays as the fallback, now ordered most-specific-first.
 */
function browserLabel(): string {
  const brands = (navigator as Navigator & { userAgentData?: { brands?: Array<{ brand: string }> } })
    .userAgentData?.brands?.map((b) => b.brand)
  if (brands?.length) {
    if (brands.some((b) => /Microsoft Edge/i.test(b))) return 'edge'
    if (brands.some((b) => /Opera|OPR/i.test(b))) return 'opera'
    if (brands.some((b) => /Google Chrome/i.test(b))) return 'chrome'
    if (brands.some((b) => /Chromium/i.test(b))) return 'chromium'
  }

  const ua = navigator.userAgent
  if (/Edg[A-Z]?\//.test(ua) || /EdgiOS/.test(ua)) return 'edge'
  if (/OPR\/|Opera/.test(ua)) return 'opera'
  if (/CriOS|Chrome/.test(ua)) return 'chrome'
  if (/FxiOS|Firefox/.test(ua)) return 'firefox'
  if (/Safari/.test(ua)) return 'safari'
  return 'other'
}

/**
 * The browser's MAJOR version, or undefined when nothing trustworthy is on offer.
 *
 * Without this the compatibility floor is an article of faith. `browser: 'edge'`
 * cannot answer the only question that matters when a class fails on a school PC
 * in Cairo — "was this an old Edge?" — and it cannot tell us whether anyone is
 * actually down at the 84 we pay to support, or whether the real floor in the
 * field is 110 and half our polyfills are dead weight.
 *
 * Read from userAgentData.brands where present (survives UA reduction), then from
 * the UA string most-specific-first, for the same reason browserLabel is ordered
 * that way: Edge and Opera both carry "Chrome/<version>", so a Chrome-first match
 * would report Edge's Chromium version rather than Edge's own.
 */
function browserVersion(): string | undefined {
  const brands = (
    navigator as Navigator & {
      userAgentData?: { brands?: Array<{ brand: string; version: string }> }
    }
  ).userAgentData?.brands
  if (brands?.length) {
    const named =
      brands.find((b) => /Microsoft Edge/i.test(b.brand)) ??
      brands.find((b) => /Opera|OPR/i.test(b.brand)) ??
      brands.find((b) => /Google Chrome/i.test(b.brand)) ??
      brands.find((b) => /Chromium/i.test(b.brand))
    // "Not A;Brand" entries carry deliberate junk versions; only trust a match.
    if (named?.version) return named.version.split('.')[0]
  }

  const ua = navigator.userAgent
  const match =
    /Edg(?:[A-Z]{1,3})?\/(\d+)/.exec(ua) ??
    /OPR\/(\d+)/.exec(ua) ??
    /(?:CriOS|Chrome)\/(\d+)/.exec(ua) ??
    /(?:FxiOS|Firefox)\/(\d+)/.exec(ua) ??
    /Version\/(\d+).*Safari/.exec(ua)
  return match?.[1]
}

function osLabel(): string {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  if (/Mac OS X/.test(ua)) return 'macos'
  if (/Windows/.test(ua)) return 'windows'
  return 'other'
}

/**
 * OS version, as a SEPARATE field rather than folded into `os`.
 *
 * Deliberately additive: `os` has been emitting the bare family since the funnel
 * was built, and re-shaping it into `windows-10.0` would silently break every
 * existing query that filters `os = "windows"`.
 *
 * It earns its place on Windows, where the OS version is a hard ceiling on the
 * browser version: Windows 7 and 8.1 cannot go above Chrome/Edge 109 however
 * diligent the user is. So a failure on `6.1` is a different conversation from
 * the same failure on `10.0` — one is "update your browser", the other is "this
 * machine cannot be updated and needs a different answer from us".
 *
 * `10.0` covers both Windows 10 and 11: the UA genuinely cannot tell them apart,
 * and inventing a distinction would be a fabricated dimension.
 */
function osVersion(): string | undefined {
  const ua = navigator.userAgent
  return (
    /Windows NT ([\d.]+)/.exec(ua)?.[1] ??
    /Android (\d+)/.exec(ua)?.[1] ??
    /(?:iPhone )?OS (\d+)[_\d]* like Mac/.exec(ua)?.[1] ??
    /Mac OS X (\d+)[_\d]*/.exec(ua)?.[1]
  )
}

/** Effective type (4g/3g) and access (wifi/cellular) where the browser exposes them. */
function networkInfo(): { effectiveType?: string; type?: string } {
  const connection = (navigator as { connection?: { effectiveType?: string; type?: string } }).connection
  return { effectiveType: connection?.effectiveType, type: connection?.type }
}

export function report(credential: Credential, options: ReportOptions): void {
  const net = networkInfo()
  const body = JSON.stringify({
    code: credential.code ?? undefined,
    reconnect_token: credential.reconnectToken ?? undefined,
    stage: options.stage,
    outcome: options.outcome ?? 'ok',
    error_kind: options.errorKind,
    duration_ms: options.durationMs,
    failure_id: options.failureId,
    ice: options.ice,
    permission: options.permission,
    join_attempt_id: JOIN_ATTEMPT_ID,
    browser: browserLabel(),
    browser_version: browserVersion(),
    os: osLabel(),
    os_version: osVersion(),
    network: net.effectiveType,
    network_type: net.type,
    meet_version: MEET_VERSION,
  })

  const url = `${API_HOST}${DIAGNOSTICS_PATH}`

  // ONE transport, deliberately: `fetch` with `keepalive`.
  //
  // This used to try `sendBeacon` first with a cross-origin Blob typed
  // application/json, ignore its boolean return, and `return` unconditionally —
  // making the fetch below unreachable in every browser that has sendBeacon,
  // i.e. all of them. On 2026-08-02 not a single client report reached
  // production all day across 8 real classes, while the fetch-based exchange to
  // the same API host from the same origin worked fine. A class where nobody
  // could hear anything was indistinguishable from a perfect one.
  //
  // Beacon is not merely demoted to the unload path, because that path is where
  // it is least verifiable: a non-safelisted content type makes the beacon a
  // preflighted cross-origin request, and `true` only means "queued", never
  // "delivered" — so the one stage that fires during unload (`left`) would keep
  // vanishing silently and we would not know. `keepalive` is specified to let a
  // request outlive the document, which is the only property beacon had over
  // fetch.
  //
  // The exception: keepalive is not universal (the support gate is
  // feature-based and admits Firefox, which only shipped it recently), and a
  // fetch without it is genuinely unreliable during unload. So when keepalive is
  // missing, `left` falls back to a beacon — worse observability than fetch, but
  // strictly better than dropping the report.
  if (!KEEPALIVE_SUPPORTED && options.stage === 'left' && navigator.sendBeacon) {
    try {
      if (navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return
    } catch {
      /* fall through to fetch */
    }
  }

  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    keepalive: true,
    body,
  }).catch(() => {
    /* telemetry is advisory — never surface a failure to the user */
  })
}
